import {
    Alternatives,
    Facet,
    Filter,
    FilterGroup,
    ProductCollection,
    ProductCollectionResponse,
} from "@hermes/api-model-catalog";
import { EditoResponse } from "@hermes/api-model-editorial";
import { GridProduct } from "@hermes/api-model-product";

import {
    GridAlternatives,
    GridData,
    GridEdito,
    GridFacet,
    GridFilter,
    GridFilterGroup,
} from "../../store/grid.store.model";

/**
 * these mappers are used to transform Grid Model complexe object class into simpler object
 *
 * Equivalents :
 * - GridData <=> ProductCollecionResponse
 * - GridFilter <=> Filter
 * - GridFilterGroup <=>  FilterGroup
 * - GridProduct <=> Product
 * - GridEdito <=> Edito
 * - GridAlternatives <=> Alternatives
 * - GridFacet <=> Facet
 */

export const gridEditosFromEditoResponse = (
    editos: EditoResponse[] | undefined,
): GridEdito[] =>
    editos
        ? editos.map((edito: EditoResponse) => ({
              nodeId: edito.nodeId,
              nodeUrl: edito.nodeUrl,
              contentTitle: edito.contentTitle,
              contentBody: edito.contentBody,
              contentTokens: edito.contentTokens,
              mediaType: edito.mediaType,
              imageRetouched: edito.imageRetouched,
              imageMobileUrl: edito.imageMobileUrl,
              imageDesktopUrl: edito.imageDesktopUrl,
              imageDesktopAlt: edito.imageDesktopAlt,
              videoAlt: edito.videoAlt,
              alwaysShowControls: edito.alwaysShowControls,
              videoLoopMuteAutoplay: edito.videoLoopMuteAutoplay,
              videoUrl: edito.videoUrl,
              imageMobileAlt: edito.imageMobileAlt,
              token: edito.token,
          }))
        : [];

export const gridDataFromProductCollectionResponse = (
    response: ProductCollectionResponse,
): GridData => ({
    total: response.total,
    url: response.url,
    redirectionUrl: response.redirectionUrl,
    products: gridProductsFromProductCollection(response.productCollection),
    productsByCategory: gridProductsByCategoryFromProductCollection(
        response.productCollection,
    ),
    alternatives: gridAlternativesFromAlternatives(response.alternatives),
    filterGroups: gridFilterGroupsFromFilterGroups(response.filters),
    editos: gridEditosFromEditoResponse(response.editorials),
    facet: gridFacetFromFacet(response.facet),
    isHeroProduct: response.isHeroProduct,
});

export const gridProductsFromProductCollection = (
    productCollection: ProductCollection,
): GridProduct[] => productCollection?.items ?? [];

export const gridProductsByCategoryFromProductCollection = (
    productCollection: ProductCollection,
): Record<string, GridProduct[]> | undefined =>
    productCollection?.itemsByCategory;

export const gridAlternativesFromAlternatives = (
    alternatives: Alternatives,
): GridAlternatives => {
    if (!alternatives) {
        return {} as GridAlternatives;
    }
    return {
        originalSearch: alternatives.originalSearch,
        resultsFor: alternatives.resultsFor,
        total: alternatives.total,
    };
};

export const gridFiltersFromFilters = (filters: Filter[]): GridFilter[] => {
    if (!(filters && filters.length > 0)) {
        return [];
    }
    return filters.map((filter: Filter) => ({
        name: filter.name,
        selected: filter.selected,
        total: filter.total,
        urlParams: filter.urlParams,
        value: filter.value,
        filterGroupName: filter.filterGroupName,
        facetIsCrawlable: filter.facetIsCrawlable === true,
        combinedFilterImage: filter.combinedFilterImage,
        gridHighlightImage: filter.gridHighlightImage,
        filterImage: filter.filterImage,
    }));
};

export const gridFilterGroupsFromFilterGroups = (
    filterGroups: FilterGroup[],
): GridFilterGroup[] => {
    if (!(filterGroups && filterGroups.length > 0)) {
        return [];
    }

    return filterGroups.map((filterGroup: FilterGroup) => ({
        name: filterGroup.name,
        attribute: filterGroup.attribute,
        displayHint: filterGroup.displayHint,
        presentation: filterGroup.presentation,
        isGridHighlight: filterGroup.isGridHighlight,
        isHiddenInFilterBar: filterGroup.isHiddenInFilterBar,
        facetUrlParameterName: filterGroup.facetUrlParameterName ?? "",
        seoPriority: filterGroup.seoPriority,
        filters: gridFiltersFromFilters(filterGroup.filters),
    }));
};

export const gridFacetFromFacet = (facet: Facet | undefined): GridFacet => {
    if (!facet) {
        return {} as GridFacet;
    }

    return {
        title: facet.title,
        name: facet.name,
        path: facet.path,
        altPaths: facet.altPaths,
        error: facet.error,
    };
};
